<template>
  <div
    class="rag-story project-case-study"
    :style="{ '--case-chrome': chromeHeight + 'px' }"
  >
    <div class="case-hero case-hero-text">
      <ProjectPageHeader project-id="pwcRag" context="PwC">
        <h1>A Structure-Preserving RAG Ingestion Pipeline</h1>
        <p class="project-subtitle">
          Preserving document structure when preparing PDFs for an AI assistant
        </p>
      </ProjectPageHeader>
      <div class="case-hero-lede">
        <p class="lede">
          At PwC, I evaluated and combined document-processing tools for an AI
          assistant that answers public enquiries using a client’s documents.
          I built an ingestion pipeline that prepares those documents for
          retrieval while preserving the relationships an answer may depend
          on, including table structure, cross-page references and the branches
          of a flowchart. End-to-end tests and deployment analysis informed the
          solution I recommended to the client.
        </p>
      </div>
    </div>
    <ProjectChapterNav
      ref="chapterNav"
      :chapters="chapters"
      :start-number="0"
      :active-chapter="activeChapter"
      :progress="chapterProgress"
      @navigate="goToChapter"
    />
    <article class="project-narrative case-narrative">
      <section id="case-brief" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>00</span>The brief</span>
          <h2 id="understanding-the-client-requirements" data-source-anchor="understanding-the-client-requirements">Understanding what the client needed</h2>
        </header>
        <div class="case-prose-pair" data-case-reveal>
          <p>The assistant would find relevant passages in the client’s documents and give them to a language model to help answer a question, an approach known as retrieval-augmented generation (RAG). My task was to prepare the documents for that process. The requirements covered paragraphs and tables continuing across pages, complex table structures, references to distant notes and remarks, and flowcharts stored as images. An answer could depend on a relationship within any of these, so those relationships needed to survive document processing.</p>
          <p>These functional requirements left the technical choices open. I needed to establish which existing tools could meet them, what additional processing was necessary, and how the combined solution would fit the client’s infrastructure and operating budget. I brought the cases together in a controlled five-page document, so comparisons used the same input and could follow the evidence through to an answer.</p>
        </div>
      </section>
      <section id="case-chapter-0" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>01</span>Page breaks</span>
          <h2
            class="project-chapter-first"
            id="why-one-pdf-needed-several-ways-of-reading"
            data-source-anchor="why-one-pdf-needed-several-ways-of-reading"
          >
            Keeping a paragraph or table together across pages
          </h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
          <p>
            A page boundary can fall halfway through a paragraph or table. If
            extraction treats the next page as a fresh start, the resulting
            fragments may separate a passage from its continuation. I first tested
            how much an existing parser could recover. MinerU, a tool for
            converting PDFs into structured content, rejoined the interrupted
            paragraphs and tables in the test document. This requirement could
            be met within the parsing stage.
          </p>
          <p>
            The next check was whether that continuity would survive chunking,
            which divides the parsed content into searchable passages. A parser
            could reconstruct a table successfully, only for a later split to
            detach its rows from their context. I included this downstream
            behaviour in the selection criteria, checking both the reconstructed
            document and the passages produced from it.
          </p>
        </div>
      </section>
      <section id="case-chapter-1" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>02</span>Complex tables</span>
          <h2 id="complex-tables" data-source-anchor="complex-tables">
            Preserving the structure inside a table
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              A merged cell can make one heading apply to several rows or
              columns. A nested table introduces a smaller set of relationships
              inside a single cell, such as the booking rules in this example.
              Flattening either into a sequence of words makes it harder to
              determine which condition governs which value. I tested both
              structures separately because success on merged cells would say
              little about a parser’s ability to recognise a table inside a
              table.
            </p>
            <p>
              MinerU’s OCR-based path, which recognises text and reconstructs
              layout, recovered the merged cells as HTML but missed the nested
              tables in the tests. Its built-in vision-language model (VLM),
              which interprets images as well as text, recovered both. HTML
              could express the resulting hierarchy explicitly through cells
              spanning rows or columns and tables contained within other cells.
            </p>
            <p>
              That result needed to survive the rest of the pipeline. In the
              RAGFlow path I tested, subsequent processing discarded the HTML
              tags. The parser’s output contained the relationships, but the
              material supplied for an answer had lost them. With LlamaIndex,
              a framework for organising content for retrieval, the structure
              remained available through indexing, retrieval and
              answer generation. I therefore selected MinerU’s built-in VLM
              with LlamaIndex, checking the evidence returned to the model as
              well as the parser’s output.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content"><TableStructureDemo /></div>
          </div>
        </div>
      </section>
      <section id="case-chapter-2" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>03</span>References</span>
          <h2 id="notes" data-source-anchor="notes">
            Retrieving a passage with the note it refers to
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              A sentence could cite Note (4) several pages before the note
              appeared. Extracting both passages accurately still left retrieval
              with two separate pieces. A question might match the citing passage closely
              while giving the search little reason to return the note it refers to.
              I needed to make their explicit reference part of the material
              being indexed.
            </p>
            <p>
              I considered linking the resulting chunks through metadata and
              attaching the note to its citing text immediately after parsing.
              The second approach was simpler to carry through retrieval. MinerU
              preserved the reference metadata, and the document’s surrounding
              context was still available at that point. A custom Python linker
              could resolve the relationship before chunk boundaries were set.
            </p>
            <p>
              Repeated note numbers meant that matching a label alone was
              insufficient. The linker used the relevant section, position and
              surrounding context, repaired fragmented notes, and normalised
              inconsistent labels. It then placed the note beside its citation
              and removed the duplicate standalone copy. Chunking could keep
              the passage and its linked note in one unit of evidence.
            </p>
            <p>
              The example connects a sentence on page 3 with its note on page 5.
              Across the controlled document, the linker resolved all 13
              cross-page references. In the retrieval check, the combined
              passage and note became the top result for the question they
              answered.
            </p>
            <div class="impl-details">
              <button
                class="impl-details-toggle"
                type="button"
                :aria-expanded="referenceResearchOpen"
                aria-controls="reference-research"
                @click="referenceResearchOpen = !referenceResearchOpen"
              >
                Other approaches I tested
                <span class="impl-details-caret" aria-hidden="true"></span>
              </button>
              <div
                id="reference-research"
                class="impl-details-panel"
                :class="{ 'is-open': referenceResearchOpen }"
                :inert="!referenceResearchOpen"
                :aria-hidden="!referenceResearchOpen"
              >
                <div class="impl-details-content">
                  <p>
                    Baidu Unlimited OCR offered a multi-page mode. In tests on difficult
                    public government documents, it retained many layout features, while the
                    relationship between a passage and a later note still needed explicit
                    linking. Reading several pages together did not by itself establish which
                    passages belonged together.
                  </p>
                  <p>
                    I also examined image-based and multimodal retrieval.
                    Keeping each page as an image retained its visual layout
                    while leaving cross-page relationships separate. The
                    RAGFlow path I evaluated converted images to text before
                    embedding them. Linking the references in that text let me
                    determine which pieces would be retrieved together.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content"><ReferenceLinkingDemo /></div>
          </div>
        </div>
      </section>
      <section id="case-chapter-3" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>04</span>Flowcharts</span>
          <h2 id="mermaid" data-source-anchor="mermaid">
            Making a flowchart’s decision logic searchable
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              Many client documents used flowchart images to explain a
              procedure. An answer might depend on following a “Yes” branch
              through several conditions. OCR could recover the words in each
              box while losing the arrows that made them a decision. I also
              tested free-form VLM descriptions, which conveyed the overall
              process with varying levels of detail.
            </p>
            <p>
              I chose Mermaid, a text-based diagram language that makes each
              branch explicit through nodes, connecting edges and labels.
              This let me inspect the individual decision paths. A VLM could convert the image
              into this representation, making its logic searchable alongside
              the document’s prose. Rendering the text back into a diagram also
              gave me a way to compare the reconstructed decisions with the
              original. In the controlled test, the reconstruction retained every node, edge and
              label and supported correct answers to both flowchart questions.
            </p>
            <p>
              Later in the research, I found that MinerU’s built-in VLM already
              supported converting flowcharts to Mermaid. It could select an
              appropriate representation for an image, including HTML for tables
              and Mermaid for flowcharts. This brought the two visual
              requirements into the same parser and model, fitting the client’s
              preference to keep the number of models they operated small.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content"><FlowchartReconstructionDemo /></div>
          </div>
        </div>
      </section>
      <section id="case-solution" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>05</span>The solution</span>
          <h2 id="what-i-tested-before-recommending-it" data-source-anchor="what-i-tested-before-recommending-it">Bringing the four requirements into one pipeline</h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
            <p>
              The recommended architecture combined MinerU with its built-in
              VLM, a post-parsing reference linker, and LlamaIndex.
              Parsing recovered document continuity and represented tables and
              diagrams in structured text. The linker attached referenced notes and remarks before LlamaIndex formed chunks for retrieval.
              An embedding model represented those chunks and the question as
              numerical vectors, allowing passages with related meanings to be
              found even when their wording differed. The answer model then
              received the retrieved evidence with its relationships intact.
            </p>
            <p>
              I integrated these components in a proof of concept and followed
              the controlled document through the full path, checking what
              reached the answer model after each processing choice. This
              exposed losses that a successful parsing preview had concealed and
              gave the client an end-to-end basis for choosing the tools.
            </p>
            <p>
              I separated GPU-backed parsing and visual interpretation from CPU orchestration,
              reference linking and indexing, then compared on-premises and private-cloud
              deployment. On-demand processing could release resources between ingestion jobs.
              Starting a new job would then take time to load the model. Keeping the service
              running traded that delay for ongoing cost. These choices let the client assess
              the pipeline against its infrastructure, ingestion frequency and maintenance
              capacity.
            </p>
          </div>
      </section>
      <section id="case-chapter-4" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>06</span>Reflection</span>
          <h2 id="judging-the-whole-solution" data-source-anchor="judging-the-whole-solution">
            Judging the whole solution
          </h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
          <p>
            Part of my role was deciding what would count as meeting a
            requirement. “Support complex tables” leaves several possibilities
            open. A tool might reproduce a table’s appearance, recover its
            hierarchy, or make that hierarchy available to the model answering
            a question. The client’s purpose determines which of these matters.
            I had to translate that purpose into checks detailed enough to guide
            a technical choice, while leaving room to discover how existing
            tools could satisfy it. Specifying a particular implementation too
            early would have narrowed the options before I understood them.
          </p>
          <p>
            I also needed to judge the combination of tools as a solution in its
            own right. A capability demonstrated by one component can disappear
            at its interface with the next, as the table structure did during
            downstream processing. That makes integration part of the
            evaluation itself. It affects where to intervene, too. Existing
            parsing capabilities covered several requirements, while reference
            linking called for a small piece of custom logic. The distinction
            depended on the relationship each requirement needed to preserve.
            It gave me a reason for every part I added and a way to test whether
            that addition worked.
          </p>
          <p>
            Operating constraints belong in the same reasoning. Ingestion frequency changes
            whether model start-up time or idle GPU cost is the more significant concern. A
            preference for fewer models affects the value of finding table and flowchart
            support within one parser. These dependencies mean a recommendation has to explain
            the conditions under which its choices make sense. I want the client to be able to
            see which decisions can remain in place as usage grows, and which should be
            revisited if its documents, workload or infrastructure change.
          </p>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
import projectCaseStudyMixin from "../mixins/projectCaseStudyMixin";
import ProjectChapterNav from "./ui/ProjectChapterNav.vue";
import ProjectPageHeader from "./ui/ProjectPageHeader.vue";
import TableStructureDemo from "./view/TableStructureDemo.vue";
import ReferenceLinkingDemo from "./view/ReferenceLinkingDemo.vue";
import FlowchartReconstructionDemo from "./view/FlowchartReconstructionDemo.vue";

export default {
  name: "RagflowPipelinePage",
  mixins: [projectCaseStudyMixin],
  components: {
    ProjectChapterNav,
    ProjectPageHeader,
    ReferenceLinkingDemo,
    TableStructureDemo,
    FlowchartReconstructionDemo,
  },
  data() {
    return { referenceResearchOpen: false };
  },
  computed: {
    chapters() {
      return [
        { id: "case-brief", label: "Brief" },
        { id: "case-chapter-0", label: "Page breaks" },
        { id: "case-chapter-1", label: "Tables" },
        { id: "case-chapter-2", label: "References" },
        { id: "case-chapter-3", label: "Flowcharts" },
        { id: "case-solution", label: "Solution" },
        { id: "case-chapter-4", label: "Reflection" },
      ];
    },
  },
};
</script>

<style scoped>
.case-stage-content :deep(.link-demo) { margin-top: 0; }
</style>
